import { useEffect, useState } from "react";
import api from "@/lib/api";
import { downloadPdf } from "@/lib/files";
import { fmtDA } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { FileDown, BarChart3, Loader2 } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const COLORS = ["#94a3b8", "#f59e0b", "#16a34a", "#0891b2"];
const fq = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(n || 0);
const pct = (v) => (v === null || v === undefined ? "—" : `${v >= 0 ? "+" : ""}${fq(v)} %`);

const MiniTable = ({ head, rows, testid }) => (
  <div className="overflow-x-auto" data-testid={testid}>
    <table className="w-full text-sm">
      <thead><tr className="text-left text-muted-foreground border-b">
        {head.map((h, i) => <th key={h} className={`py-2 pr-2 font-medium ${i > 1 ? "text-right" : ""}`}>{h}</th>)}
      </tr></thead>
      <tbody className="divide-y">
        {rows.map((r, i) => (
          <tr key={i}>{r.map((c, j) => <td key={j} className={`py-2 pr-2 ${j > 1 ? "tabular text-right" : ""}`}>{c}</td>)}</tr>
        ))}
      </tbody>
    </table>
  </div>
);

const SocieteStats = ({ s, periode }) => (
  <div className="mb-8 last:mb-0" data-testid={`stats-societe-${s.societe_id}`}>
    <p className="text-sm font-semibold mb-3">{s.societe}</p>
    <h4 className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Synthèse annuelle</h4>
    <MiniTable testid={`stats-synthese-${s.societe_id}`}
      head={["Année", "Nb factures", "CA HT", "TVA collectée", "Droit de timbre", "Total TTC", "Évolution CA HT"]}
      rows={s.synthese.map((r) => [r.annee, r.nb, fmtDA(r.ht), fmtDA(r.tva), fmtDA(r.timbre), fmtDA(r.ttc),
        <span key="e" className={r.evolution > 0 ? "text-success" : r.evolution < 0 ? "text-destructive" : ""}>{pct(r.evolution)}</span>])} />
    <h4 className="text-xs uppercase tracking-wide text-muted-foreground mt-5 mb-2">CA HT mensuel</h4>
    <div className="h-64" data-testid={`stats-chart-${s.societe_id}`}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={s.mois}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="mois" tickFormatter={(m) => m.slice(0, 4)} fontSize={11} />
          <YAxis fontSize={11} tickFormatter={(v) => `${Math.round(v / 1e6)} M`} />
          <Tooltip formatter={(v) => fmtDA(v)} />
          <Legend />
          {s.years.map((y, i) => <Bar key={y} dataKey={y} fill={i === s.years.length - 1 ? "#2563eb" : COLORS[i % COLORS.length]} radius={[3, 3, 0, 0]} />)}
        </BarChart>
      </ResponsiveContainer>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-5">
      <div>
        <h4 className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Top 10 clients — {periode}</h4>
        <MiniTable testid={`stats-top-clients-${s.societe_id}`} head={["#", "Client", "Factures", "CA HT", "Part"]}
          rows={s.top_clients.map((c, i) => [i + 1, c.nom, c.nb, fmtDA(c.ht), `${fq(c.part)} %`])} />
      </div>
      <div>
        <h4 className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Top produits — {periode}</h4>
        <MiniTable testid={`stats-top-produits-${s.societe_id}`} head={["#", "Produit", "Quantité", "CA HT", "Part"]}
          rows={s.top_produits.map((p, i) => [i + 1, p.produit, fq(p.q), fmtDA(p.ht), `${fq(p.part)} %`])} />
      </div>
    </div>
  </div>
);

export const StatistiquesCard = ({ societeId, exercice }) => {
  const [data, setData] = useState(null);
  const params = { ...(societeId !== "all" && { societe_id: societeId }), ...(exercice !== "all" && { annee: exercice }) };
  const periode = exercice === "all" ? "toutes années" : `exercice ${exercice}`;

  useEffect(() => {
    setData(null);
    api.get("/pdf/statistiques/data", { params }).then((r) => setData(r.data)).catch(() => setData([]));
  }, [societeId, exercice]); // eslint-disable-line

  return (
    <div className="rounded-xl border bg-card p-5 mt-6" data-testid="dashboard-statistiques">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-primary" />
          <h3 className="font-heading font-bold">Statistiques des ventes</h3>
        </div>
        <Button variant="outline" size="sm" className="gap-2" data-testid="statistiques-pdf-btn"
          onClick={() => downloadPdf(`/pdf/statistiques?${new URLSearchParams(params).toString()}`, "statistiques-ventes")}>
          <FileDown className="h-4 w-4" /> PDF
        </Button>
      </div>
      {data === null
        ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        : data.map((s) => <SocieteStats key={s.societe_id} s={s} periode={periode} />)}
    </div>
  );
};
